import { ChatMessage } from "@cryptors/api-client";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  InputLabel,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  styled,
  TooltipProps,
  tooltipClasses,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useGo } from "@refinedev/core";
import { FC } from "react";
import { Control, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

import { ChatMessageShow } from "@/pages/chatMessage/ChatMessageShow";
import { getTranslation } from "@/utils/translation";

import { ChatMessageFormValues } from "./chat-message-form.types";

type ChatMessageChildPath =
  | "chatMessageChildrenId"
  | `question.propositions.${number}.chatMessageId`;

interface ChatMessageChildrenSelectorProps {
  control: Control<ChatMessageFormValues>;
  name: ChatMessageChildPath;
  autocompleteProps: { options: readonly ChatMessage[] };
  label?: string;
}

const ChatMessageChildrenSelector: FC<ChatMessageChildrenSelectorProps> = ({
  control,
  name,
  autocompleteProps,
  label = "Enfant",
}) => {
  const { cryptorId } = useParams();
  const go = useGo();
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={undefined}
      render={({ field }) => {
        const options = autocompleteProps.options ?? [];
        const hasCurrent =
          field.value != null &&
          options.some((option) => option.id === field.value);
        const mergedOptions =
          hasCurrent || field.value == null
            ? options
            : [...options, { id: field.value, textTranslation: undefined }];
        return (
          <Stack direction="row" alignItems="center" gap={2} minWidth={200}>
            <FormControl margin="normal" style={{ flex: 1 }}>
              <InputLabel id="children-select-label">{label}</InputLabel>
              <Select
                {...field}
                value={field.value ?? ""}
                labelId="children-select-label"
                label="Enfant"
              >
                {[{ id: null, text: "Aucun" }, ...mergedOptions].map(
                  (option) => (
                    <MenuItem key={option.id ?? "none"} value={option.id ?? ""}>
                      <CustomWidthTooltip
                        title={
                          option.id ? (
                            <ChatMessageShow idProps={option.id} />
                          ) : null
                        }
                        placement="top-end"
                      >
                        <Stack flex={1}>
                          {option.id}{" "}
                          {"text" in option
                            ? option.text
                            : getTranslation(option.textTranslation)?.value}
                        </Stack>
                      </CustomWidthTooltip>
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <IconButton
              onClick={() => {
                go({
                  to: `/cryptors/${cryptorId}/chat-message/${field.value}`,
                });
              }}
              disabled={!field.value}
            >
              <OpenInNewIcon />
            </IconButton>
          </Stack>
        );
      }}
    />
  );
};

export default ChatMessageChildrenSelector;

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 700,
  },
});
